import go from "gojs";
import { makeAutoObservable, reaction, runInAction } from "mobx";
import agent from "../api/agent";
import { enumDescription, RBDItemType, RBDTransmissionType, RBDType } from "../config/enum";
import { IEquipment, IStation, ISystem } from "../models/asset";
import { DiagramLinkData, DiagramNodeData, IRBDDiagram, IRBDElement, IRBDElementRequest, IRBDItem, IRBDItemRow, IRBDTabelRequest, IRBDTransmission, IRBDTransmissionRequest } from "../models/rbd";
import { diagram as diagramConstants } from '../../app/component/features/diagram/constants'
import { IDiagramValues } from "../component/features/diagram/Diagram";

export default class RBDStore {
    selectedRbdType?: RBDType = undefined;
    selectedId?: number = undefined;

    // untuk item filter
    selectedItemType: RBDItemType = RBDItemType.ELEMENT;
    selectedTransmissionType: RBDTransmissionType = RBDTransmissionType.SERIAL;
    selectedStation: IStation | null = null;
    selectedSystem: ISystem | null = null;
    selectedEquipment: IEquipment | null = null;
    selectedTransmissionItems: IRBDItem[] = [];

    // dropdown dan loadingnya
    dropdownSystem: ISystem[] = [];
    dropdownEquipment: IEquipment[] = [];
    loadingStation = false;
    loadingSystem = false;
    loadingEquipment = false;

    // tables
    rbdItems: IRBDItem[] = [];
    rbdItemRows: IRBDItemRow[] = [];
    loadingTable = false;

    // button states (edit / add to table)
    isEdit = false;
    editedItem: IRBDItem | null = null;

    // utk save
    saveDisabled = true;
    loadingSave = false;

    // diagram 
    nodeDataArray: DiagramNodeData[] = []
    linkDataArray: DiagramLinkData[] = []
    goDiagram: go.Diagram | null = null
    goDiagramMaker?: any = undefined
    isDiagramDraggingToolEnabled = false
    loadingDiagram = false
    displayDiagram = false
    diagramInit = () => {
        const $ = go.GraphObject.make;
        // set your license key here before creating the diagram: go.Diagram.licenseKey = "...";
        const diagram =
            $(go.Diagram, {
                model: new go.GraphLinksModel({ linkKeyProperty: 'key' }),
            });
        diagram.initialDocumentSpot = go.Spot.Center
        diagram.initialViewportSpot = go.Spot.Center
        diagram.undoManager.isEnabled = true
        diagram.nodeTemplate = 
            $(go.Node, 'Auto',
                new go.Binding('width', 'containerWidth'),
                new go.Binding('location', 'loc', go.Point.parse).makeTwoWay(go.Point.stringify),
                $(go.Shape, 
                    { height: 50, strokeWidth: 1 },
                    new go.Binding('width'),
                    new go.Binding('stroke', 'strokeColor'),
                    new go.Binding('fill', 'color'),
                    new go.Binding('figure'),
                ),
                $(go.TextBlock, 
                    { stroke: 'white', editable: false, textAlign: 'center' }, 
                    new go.Binding('text')
                ),
            );
        diagram.linkTemplate = 
            $(go.Link,
                { routing: go.Link.AvoidsNodes, corner: 5 },
                $(go.Shape, { strokeWidth: 2 }, new go.Binding('stroke', 'color')),
                $(go.Shape, 
                    { toArrow: 'Triangle', fill: diagramConstants.link.color.show, stroke: diagramConstants.link.color.show }
                ),
            );
        diagram.toolManager.draggingTool.isEnabled = this.isDiagramDraggingToolEnabled

        runInAction(() => {
            this.goDiagramMaker = $
            return this.goDiagram = diagram
        })
        
        return diagram
    }

    constructor() {
        makeAutoObservable(this);

        reaction(() => [this.selectedRbdType, this.selectedId, this.selectedStation, this.dropdownSystem],
            values => {
                if (this.selectedRbdType && this.selectedId && this.selectedStation) {

                    // system nya diset sesuai dgn system yg dipilih
                    if (this.selectedRbdType === RBDType.SYSTEM && this.dropdownSystem.length > 0) { // saat dropdown system udah dapet, dan tipe RBD sistem
                        this.setSelectedSystem(this.dropdownSystem.find(x => x.id === this.selectedId) ?? null);
                    }
                    if (this.dropdownSystem.length === 0) { // saat station udah dapet data, tapi system belum ke-fetch
                        // ambil data tabelnya
                        this.getTable(this.selectedRbdType, this.selectedId);
                    }
                }
            }
        )

        reaction(() => this.rbdItems,
            rbdItems => {
                // sinkronin antara RBD Items dan yg ditampilkan di table rows
                let elementCount = 1, transSerialCount = 1, transParallelCount = 1, transRedundantCount = 1, transSharingLoadCount = 1;
                this.rbdItemRows = this.rbdItems.map((rbdItem, index) => {

                    // buat ngitung kolom "No" yg 1, 2, 3, S1, S2, ...
                    rbdItem.sequence = index;
                    if (rbdItem.itemType === RBDItemType.ELEMENT && rbdItem.element) {
                        rbdItem.name = elementCount.toString();
                        elementCount++;
                    } else if (rbdItem.itemType === RBDItemType.TRANSMISSION && rbdItem.transmission) {
                        if (rbdItem.transmission.transmissionType === RBDTransmissionType.SERIAL) {
                            rbdItem.name = "S" + transSerialCount.toString();
                            transSerialCount++;
                        } else if (rbdItem.transmission.transmissionType === RBDTransmissionType.PARALLEL) {
                            rbdItem.name = "P" + transParallelCount.toString();
                            transParallelCount++;
                        } else if (rbdItem.transmission.transmissionType === RBDTransmissionType.REDUNDANT) {
                            rbdItem.name = "R" + transRedundantCount.toString();
                            transRedundantCount++;
                        } else if (rbdItem.transmission.transmissionType === RBDTransmissionType.SHARING_LOAD) {
                            rbdItem.name = "SL" + transSharingLoadCount.toString();
                            transSharingLoadCount++;
                        }
                    }

                    // buat sinkronin kolom "code", "type"
                    let code = "", type = "";
                    if (rbdItem.itemType === RBDItemType.ELEMENT && rbdItem.element) {
                        if (rbdItem.element.equipmentId && rbdItem.element.tagNumber && rbdItem.element.equipmentType) {
                            code = rbdItem.element.tagNumber;
                            type = rbdItem.element.equipmentType;
                        }
                        else if (rbdItem.element.systemId && rbdItem.element.name) {
                            code = rbdItem.element.name;
                        }
                    } else if (rbdItem.itemType === RBDItemType.TRANSMISSION && rbdItem.transmission) {
                        code = enumDescription(rbdItem.transmission.transmissionType) + " " +
                            rbdItem.transmission.elements.map(x => x.name).join(" & ");
                    }

                    return { id: rbdItem.sequence, no: rbdItem.name, code, type }
                });

                // sinkronin antara RBD Items state dengan dropdown transmission
                // reset selection (1 element terbawah)
                // this.setSelectedTransmissionItems(this.lockedTransmissionItems);
            }
        )

        // this.diagramInit()
    }

    transmissionableItems = () => {
        return this.rbdItems.filter(x =>
            (!x.isGrouped || (!!this.editedItem?.transmission?.elements && this.editedItem.transmission.elements.findIndex(y => y === x) >= 0)) && // yg bisa di-add cm yg belum di grup
            (x.sequence < (this.editedItem?.sequence ?? this.rbdItems.length)) // yg bisa di-add cm element sebelum element yg di-edit (kalo bukan edit, ya diitung dari akhir aja)
        );
    }

    get selectedTransmissionItemsSequence() {
        return this.selectedTransmissionItems.map(x => x.sequence);
    }

    setLoadingStation = (value: boolean) => {
        this.loadingStation = value;
    }

    setLoadingSystem = (value: boolean) => {
        this.loadingSystem = value;
    }

    setLoadingEquipment = (value: boolean) => {
        this.loadingEquipment = value;
    }

    setSelectedRbdType = (rbdType: RBDType) => {
        this.selectedRbdType = rbdType;
    }

    setSelectedId = (value: number) => {
        this.selectedId = value;
    }

    setSelectedItemType = (itemType: RBDItemType) => {
        this.selectedItemType = itemType;
    }

    setSelectedTransmissionType = (transmissionType: RBDTransmissionType) => {
        this.selectedTransmissionType = transmissionType;
    }

    setSelectedStation = (station: IStation | null) => {
        this.selectedStation = station;

        // dropdown station berubah, dropdown system re-fetch
        this.dropdownSystem = [];
        this.setSelectedSystem(null);
        if (station) this.getSystems(station.id)
    }

    setSelectedSystem = (system: ISystem | null) => {
        this.selectedSystem = system;

        // dropdown system berubah, dropdown equipment re-fetch
        this.dropdownEquipment = [];
        this.setSelectedEquipment(null);
        if (system) return this.getEquipments(system.id);
    }

    setSelectedEquipment = (equipment: IEquipment | null) => {
        this.selectedEquipment = equipment;
    }

    setSelectedTransmissionItems = (items: IRBDItem[]) => {
        this.selectedTransmissionItems = items;
    }

    // transmission 2 terbawah
    get lockedTransmissionItems() {
        return this.transmissionableItems().slice(-1);
    }

    // transmission yg di atas (yg belum diceklis)
    get disabledTransmissionItems() {
        return this.rbdItems.filter(x =>
            x.sequence > Math.min(...this.selectedTransmissionItems.map(x => x.sequence) ?? [0]) || // disable sequence setelah item terakhir yg dipilih
            x.sequence < Math.min(...this.selectedTransmissionItems.map(x => x.sequence) ?? [0]) - 1 // atau item sebelum item sequence yg belum dipilih
        );
    }

    setIsEdit = (value: boolean) => {
        this.isEdit = value;
    }

    setRbdItems = (items: IRBDItem[]) => {
        items.sort((a, b) => a.sequence - b.sequence);
        this.rbdItems = items;
    }

    addRbdItem = (item: IRBDItem) => {
        this.rbdItems = [...this.rbdItems, item];
    }

    getRbdItem = (sequence: number) => {
        return this.rbdItems.find(x => x.sequence === sequence);
    }

    setEditRbdItem = (item: IRBDItem) => {
        this.isEdit = true;
        this.editedItem = item;

        if (item.itemType === RBDItemType.ELEMENT && item.element) {
            this.setSelectedItemType(RBDItemType.ELEMENT);
            if (item.element.systemId && item.element.equipmentId) { // kalau yg diedit adalah equipment
                const systemId = item.element.systemId;
                const equipmentId = item.element.equipmentId;
                const curSystem = this.dropdownSystem.find(x => x.id === systemId);

                if (this.dropdownEquipment.map(x => x.id).indexOf(item.element.equipmentId) < 0) { // cek barangkali dropdown equipment nya belum dari system yg dimaksud
                    if (curSystem) this.setSelectedSystem(curSystem)?.then(() => {
                        const curEquipment = this.dropdownEquipment.find(x => x.id === equipmentId);
                        if (curEquipment) this.setSelectedEquipment(curEquipment);
                    });
                } else { // kalau dropdown nya udah ada, lgsg set aja
                    if (this.selectedSystem?.id !== systemId) { // cek dulu tp, apakah system yg dipilih udah sesuai, kalau belum, set dulu 
                        if (curSystem) this.setSelectedSystem(curSystem)?.then(() => {
                            const curEquipment = this.dropdownEquipment.find(x => x.id === equipmentId);
                            if (curEquipment) this.setSelectedEquipment(curEquipment);
                        });
                    } else {
                        const curEquipment = this.dropdownEquipment.find(x => x.id === equipmentId);
                        if (curEquipment) this.setSelectedEquipment(curEquipment);
                        this.selectedSystem = this.dropdownSystem.find(x => x.id === systemId) ?? null;
                    }
                }

            } else if (item.element.systemId) { // kalau yg diedit adalah system
                const systemId = item.element.systemId;
                const curSystem = this.dropdownSystem.find(x => x.id === systemId);
                if (curSystem) this.setSelectedSystem(curSystem);
            }
        } else if (item.itemType === RBDItemType.TRANSMISSION && item.transmission) {
            this.setSelectedItemType(RBDItemType.TRANSMISSION);
            this.setSelectedTransmissionType(item.transmission.transmissionType);
            this.setSelectedTransmissionItems(item.transmission.elements);
        }
    }

    saveEditRbdItem = () => {
        if (this.editedItem) {
            if (this.editedItem.itemType === RBDItemType.ELEMENT && this.editedItem.element) { // kalau yg diedit element
                if (this.editedItem.element.systemId && this.editedItem.element.equipmentId && this.selectedSystem && this.selectedEquipment) { // kalau yg diedit equipment
                    this.editedItem.element.equipmentId = this.selectedEquipment.id;
                    this.editedItem.element.tagNumber = this.selectedEquipment.tagNumber;
                    this.editedItem.element.systemId = this.selectedSystem.id;
                    this.editedItem.element.equipmentType = this.selectedEquipment.equipmentType;
                    this.setSelectedEquipment(null);
                } else if (this.editedItem.element.systemId && this.selectedSystem) { // kalau yg diedit system
                    this.editedItem.element.name = this.selectedSystem.name;
                    this.editedItem.element.systemId = this.selectedSystem.id;
                    this.setSelectedSystem(null);
                }
            } else if (this.editedItem.itemType === RBDItemType.TRANSMISSION && this.editedItem.transmission) { // kalau yg diedit transmission
                this.editedItem.transmission.transmissionType = this.selectedTransmissionType;
                this.editedItem.transmission.elements = this.selectedTransmissionItems;
            }
            this.editRbdItem(this.editedItem);
        }
        this.cancelEditRbdItem();
    }

    cancelEditRbdItem = () => {
        this.isEdit = false;
        this.editedItem = null;
        this.setSelectedTransmissionItems([]);
    }

    editRbdItem = (item: IRBDItem, sequence?: number) => {
        if (sequence) item.sequence = sequence;
        this.setRbdItems([...this.rbdItems.filter(x => x.sequence !== item.sequence), item]);
    }

    deleteRbdItem = (item: IRBDItem) => {
        if (item.itemType === RBDItemType.TRANSMISSION && item.transmission) {
            item.transmission.elements.sort((a, b) => a.sequence - b.sequence).forEach(transmissionElement => {
                this.editRbdItem({ ...transmissionElement, isGrouped: false });
            })
        }
        this.setRbdItems(this.rbdItems.filter(x => x !== item));
        this.cancelEditRbdItem();
    }

    setRbdItemGrouped = (items: IRBDItem[], value: boolean) => {
        this.setRbdItems(this.rbdItems.map(item => {
            if (items.indexOf(item) >= 0) {
                item.isGrouped = value;
            }
            return item;
        }));
    }

    getSystems = async (stationId: number) => {
        this.setLoadingSystem(true);
        try {
            const systems = await agent.Asset.getSystems(stationId);
            runInAction(() => {
                this.dropdownSystem = systems;
            });
        } catch (error) {
            throw error;
        } finally {
            this.setLoadingSystem(false);
        }
    }

    getEquipments = async (systemId: number) => {
        this.setLoadingEquipment(true);
        try {
            const equipments = await agent.Asset.getEquipments(systemId);
            runInAction(() => {
                this.dropdownEquipment = equipments;
            });
        } catch (error) {
            throw error;
        } finally {
            this.setLoadingEquipment(false);
        }
    }

    setLoadingTable = (value: boolean) => {
        this.loadingTable = value;
    }

    getTable = async (rbdType: RBDType, id: number) => {
        this.loadingTable = true;
        this.saveDisabled = true;
        try {
            let tables: IRBDTabelRequest[];
            if (rbdType === RBDType.STATION) {
                tables = await agent.RBD.getStationTable(id);
            } else if (rbdType === RBDType.SYSTEM) {
                tables = await agent.RBD.getSystemTable(id);
            }
            runInAction(() => {
                tables.forEach(row => {
                    const element: IRBDElement | undefined = row.element ? {
                        systemId: row.element.systemId,
                        name: row.element.name,
                        equipmentId: row.element.equipmentId,
                        tagNumber: row.element.tagNumber,
                        equipmentType: row.element.equipmentType
                    } : undefined;

                    const transmission: IRBDTransmission | undefined = row.transmission ? {
                        transmissionType: row.transmission.transmissionType,
                        elements: row.transmission!.elementSequences.map(seq => this.rbdItems.find(x => x.sequence === seq)!)
                    } : undefined;

                    const rbdItem: IRBDItem = {
                        sequence: row.sequence,
                        itemType: row.itemType,
                        name: row.name,
                        isGrouped: false,
                        element,
                        transmission
                    }
                    this.addRbdItem(rbdItem);

                    // kalau transmission, set isGrouped element yg di dalemnya
                    if (transmission) {
                        this.setRbdItemGrouped(transmission.elements, true);
                    }
                });
                this.setSaveDisabled(false);
            })
        } catch (error) {
            throw error;
        } finally {
            this.setLoadingTable(false);
        }
    }

    saveRbd = async (rbdType: RBDType, id: number) => {
        if (this.selectedStation) {
            this.loadingSave = true;
            try {
                const tabel: IRBDTabelRequest[] = this.rbdItems.map(item => {
                    const element: IRBDElementRequest | undefined = item.element ? {
                        systemId: item.element.systemId,
                        name: item.element.name,
                        equipmentId: item.element.equipmentId,
                        tagNumber: item.element.tagNumber,
                        equipmentType: item.element.equipmentType
                    } : undefined;

                    const transmission: IRBDTransmissionRequest | undefined = item.transmission ? {
                        transmissionType: item.transmission.transmissionType,
                        elementSequences: item.transmission.elements.map(x => x.sequence)
                    } : undefined;

                    return {
                        sequence: item.sequence,
                        itemType: item.itemType,
                        name: item.name,
                        element,
                        transmission
                    };
                });
                return await agent.RBD.save({
                    stationId: this.selectedStation.id,
                    systemId: (id && rbdType === RBDType.SYSTEM) ? id : null,
                    table: tabel,
                    diagram: this.saveDiagramToJson() ?? '',
                });
            } catch (error) {
                throw error;
            } finally {
                runInAction(() => {
                    this.loadingSave = false;
                });
            }
        }
    }

    setSaveDisabled = (value: boolean) => {
        this.saveDisabled = value;
    }

    resetStates = () => {
        this.cancelEditRbdItem(); // reset editing pada komponen
        this.setRbdItems([]); // reset item2nya
        this.selectedEquipment = null;
        this.selectedSystem = null;
    }

    // --- Diagram Actions start ---
    clearDiagram = () => {
        this.nodeDataArray = []
        this.linkDataArray = []
        this.displayDiagram = false
    }

    saveDiagramToJson = (): string | undefined => {
        return this.goDiagram?.model.toJson()
    }

    loadDiagramFromJson = (diagram: string) => {
        if (diagram && diagram.length !== 0) {
            const json: IRBDDiagram = JSON.parse(diagram)
            this.nodeDataArray = json.nodeDataArray
            this.linkDataArray = json.linkDataArray

            this.isDiagramDraggingToolEnabled = false
            
            return this.displayDiagram = true
        } else {
            return this.clearDiagram()
        }
    }

    injectDiagramValue = (value: IDiagramValues[]) => {
        if (this.goDiagram) {
            this.goDiagram.layout = this.goDiagramMaker(go.LayeredDigraphLayout, { 
                layerSpacing: 30, 
                linkSpacing: 10, 
            })

            this.isDiagramDraggingToolEnabled = true
        }
        
        value.forEach((item) => {
            const { parent: from, key, linkColor, ...node } = item
            
            this.nodeDataArray.push({ key: key, ...node })

            if (typeof from && typeof from !== 'undefined') {
                if (Array.isArray(from)) {
                    from.map((o) => {
                        this.linkDataArray.push({ from: o, to: key, color: linkColor })
                        return null;
                    })
                }
                if (!Array.isArray(from)) {
                    this.linkDataArray.push({ from, to: key, color: linkColor })
                }
            }
            return null;
        })
    }
    // --- Diagram Actions end ---

    getStationDiagram = async (stationId: number) => {
        this.loadingDiagram = true
        try {
            const res = await agent.RBD.getStationDiagram(stationId)
            this.loadDiagramFromJson(res.diagram)
        } catch (error) {
            this.clearDiagram()
            console.error(error)
        } finally {
            runInAction(() => this.loadingDiagram = false)
        }
    }

    getSystemDiagram = async (systemId: number) => {
        this.loadingDiagram = true
        try {
            const res = await agent.RBD.getSystemDiagram(systemId)
            this.loadDiagramFromJson(res.diagram)
        } catch (error) {
            console.error(error)
            runInAction(() => this.displayDiagram = false)
        } finally {
            runInAction(() => this.loadingDiagram = false)
        }
    }
}